import React, { useState } from "react"
import { useBlocks } from "../../hooks/use-blocks"
import { OldButton } from "@kit/design/old-button"
import Label from "@kit/design/label"
import Stack from "@kit/design/stack"
import SelectMenu from "@kit/design/select-menu"
import { ColorInput } from "@kit/design/color-picker"
import DatePicker from "react-datepicker"
import { createRequestObject } from "hooks/use-new-fetch"
import { format } from "date-fns"
import { toast } from "@kit/design/toast"
import { useSavedColors } from "hooks/use-saved-colors"

import logo from "./logo.png"
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption
} from "@reach/combobox"
import "@reach/combobox/styles.css"

export function BandsInTownSettings({ block_id }) {
  const [bandNames, setBandNames] = useState([])
  const { properties, update, remove } = useBlocks(block_id)
  const savedColors = useSavedColors()

  const handleSearch = async e => {
    const name = encodeURIComponent(e.target.value)
    update({ name: e.target.value, events: null })
    const request = createRequestObject({
      url: `/bandsintown/artists/search.json?name=${name}`
    })
    const response = await request.get()
    if (!response.data.length) {
      return
    }
    // The filter allows for no null items to render
    setBandNames(response.data.filter(artist => artist.name))
  }

  const handleSelect = async item => {
    update({ name: item })
    const name = encodeURIComponent(item)
    const request = createRequestObject({
      url: `/bandsintown/artists/${name}/events.json`
    })
    const response = await request.get()

    if (response.data.error) {
      toast({
        type: "error",
        text: `Encountered an error while trying to get the ${item} band name`
      })
      return
    }

    update({ events: response.data })
  }

  const handleStartDate = date => {
    update({ startDate: format(date, "MM/dd/yyy") })
  }

  const handleEndDate = date => {
    update({ endDate: format(date, "MM/dd/yyy") })
  }

  const updateDateRange = e => {
    update({ startDate: null, endDate: null, numberOfEvents: e.target.value })
  }

  const handleRemove = () => remove(block_id)

  return (
    <div className="px-4 pb-4">
      <Stack spacing={4}>
        <div>
          <img src={logo} alt="bandsintown logo" className="pb-2" />
          <div className="text-xs text-gray-700">
            Add your Bandsintown event feed to your email.
          </div>
        </div>

        <div>
          <Label htmlFor="bandsintown" className="font-bold">
            Band name
          </Label>
          <Combobox onSelect={handleSelect} aria-label="Band Names">
            <ComboboxInput
              className="border w-full h-10 pl-2 rounded"
              onChange={handleSearch}
              value={properties.name}
            />
            <ComboboxPopover className="shadow-popup" portal={false}>
              <ComboboxList>
                {bandNames.length ? (
                  bandNames.map((bandName, index) => {
                    const str = bandName.name
                    return (
                      <ComboboxOption key={`${str}-${index}`} value={str} />
                    )
                  })
                ) : (
                  <ComboboxOption value="No bands found" />
                )}
              </ComboboxList>
            </ComboboxPopover>
          </Combobox>
          <div className="text-xs py-2 text-gray-700">
            Your band name as it&apos;s displayed on Bandsintown.
          </div>
        </div>

        <div>
          <Label htmlFor="bandsintown" className="font-bold">
            Number of events to display
          </Label>
          <SelectMenu
            label="Bandsintown"
            id="bandsintown"
            onChange={updateDateRange}
            value={properties.numberOfEvents}
            className="w-full text-xs"
            options={[
              { label: "All upcoming events", value: "all" },
              { label: "Events between date range", value: "range" }
            ]}
          />
          {properties.numberOfEvents === "range" && (
            <div className="flex pt-6">
              <div className="pr-6">
                <div className="pb-2 font-semibold text-gray-700">
                  Start date
                </div>
                <DatePicker
                  showTimeSelect={false}
                  showPopperArrow={false}
                  timeCaption="time"
                  dateFormat="MMMM d, yyyy"
                  className="w-full"
                  value={properties.startDate || ""}
                  onChange={handleStartDate}
                />
              </div>
              <div>
                <div className="pb-2 font-semibold text-gray-700">End date</div>
                <DatePicker
                  showTimeSelect={false}
                  showPopperArrow={false}
                  timeCaption="time"
                  dateFormat="MMMM d, yyyy"
                  className="w-full"
                  value={properties.endDate || ""}
                  onChange={handleEndDate}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <Label
            htmlFor="textColor"
            className="pb-2 font-semibold text-gray-700"
          >
            Text Color
          </Label>
          <ColorInput
            value={properties.textColor}
            name={properties.name}
            onChange={color => update({ textColor: color.hex })}
            savedColors={savedColors}
          />
        </div>

        <OldButton
          size="sm"
          is="secondary"
          className="ml-auto block"
          onClick={handleRemove}
          icon="delete"
        >
          Remove
        </OldButton>
      </Stack>
    </div>
  )
}
